// src/pages/admin/DailyDeposits.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Loading } from "@common";
import { useAuth } from "@api/useAuth";
import {
  useBulkCreateDepositMatches,
  useCreateDepositMatch,
  useDeleteDepositMatch,
  useDepositMatchDismissals,
  useDepositMatches,
  useInsurancePayments,
  useBankAccounts,
  useBankDailyTransactions,
  useMultiClinicCollectionBreakdowns,
  useUploadBankCsv,
} from "@api/hooks";
import { DEPOSIT_SHEET_BANK_FIELDS, refundForField } from "@utils/depositSheetColumns";
import { roundTo2 } from "@utils/utils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { torontoToday, canManageDailyBankBalance } from "@utils";
import BankFeedCard from "./BankFeedCard";
import BankFeedsModal, { BankFeedsButton } from "./BankFeedsModal";
import CollectionBreakdownCard from "./CollectionBreakdownCard";
import DepositMatchPanel from "./DepositMatchPanel";
import { classifyBankLine, isCardCategory } from "./depositAutoMatch";
import {
  addDays,
  matchDeposits,
  MATCH_BACK_DAYS,
  MATCH_WINDOW_DAYS,
  practiceStats,
} from "./depositMatchUtils";

// One card per `bank_accounts.bank_type` the selected clinics bank with, in
// this order. Where the bank lines come from:
//   netsuite — RBC: NetSuite's BankingTransaction feed, copied daily into
//              bank_daily_transaction (txn_id "ns-<NetSuite id>").
//   sync     — Meridian: our own bank_daily_transaction rows, by bank_accounts.id.
//   csv      — BMO, Desjardins: bank_daily_transaction rows too, from CSVs
//              uploaded in the Bank feeds modal (`uploadable`). RBC takes CSVs
//              too, for days NetSuite hasn't imported yet.
//   none     — not connected yet, so no bank lines.
const FEED_TYPES = [
  { type: "RBC", code: "RBC", name: "RBC", feed: "netsuite", uploadable: true },
  { type: "BMO", code: "BMO", name: "BMO", feed: "csv", uploadable: true },
  { type: "DES", code: "DSJ", name: "Desjardins", feed: "csv", uploadable: true },
  { type: "MCU", code: "MER", name: "Meridian", feed: "sync" },
];
const FEED_BY_TYPE = new Map(FEED_TYPES.map((ft) => [ft.type, ft]));

const FEED_STATUS = {
  netsuite: {
    source: "NetSuite · nightly feed",
    status: "Live",
    tone: "ok",
    footnote: "Pulled from NetSuite nightly",
  },
  sync: {
    source: "Daily bank sync",
    status: "Live",
    tone: "ok",
    footnote: "Synced from Meridian daily",
  },
  csv: {
    source: "CSV upload",
    status: "Uploaded",
    tone: "ok",
    footnote: "From uploaded bank CSVs",
  },
  none: {
    source: "Not connected",
    status: "Coming later",
    tone: "warn",
    footnote: "Bank lines not available yet",
  },
};

// Bank lines from both sources in one shape, deposits only (money in), each
// tied back to its bank_accounts row so it can be grouped by clinic. One
// exception: a card batch that went negative (refunds beat sales, e.g. RBC
// "VSA DEP11606084" −103.63, type FEE) is kept, so it can net against the
// day's other batches of that card.
function normalizeBankLines(accounts, rbcRows, syncRows) {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const lines = [];

  for (const row of rbcRows) {
    const account = byId.get(row.bank);
    const amount = roundTo2(Number(row.amount) || 0);
    if (!account || account.bank_type !== "RBC" || amount === 0) continue;
    if (amount < 0 && !isCardCategory(classifyBankLine(row.description))) continue;
    const via = row.source === "netsuite" ? "NetSuite feed" : "RBC CSV";
    lines.push({
      // "ns-<NetSuite id>" — the key RBC lines have always been matched under;
      // a line from an uploaded RBC CSV is keyed like any other upload.
      id: row.source === "netsuite" ? row.txn_id : `bdt-${row.id}`,
      date: row.posted_date,
      account,
      description: row.description || "Bank deposit",
      amount,
      meta: [account.label, via].filter(Boolean).join(" · "),
      title: ["RBC", account.label, via].filter(Boolean).join(" · "),
    });
  }
  for (const row of syncRows) {
    const account = byId.get(row.bank);
    const amount = roundTo2(Number(row.amount) || 0);
    if (!account || amount <= 0) continue;
    const ft = FEED_BY_TYPE.get(account.bank_type);
    const bankName = ft?.name ?? account.bank_type;
    const via = ft?.feed === "csv" ? `${bankName} CSV` : `${bankName} sync`;
    lines.push({
      id: `bdt-${row.id}`,
      date: row.posted_date,
      account,
      description: row.description || "Bank deposit",
      amount,
      meta: [account.label, via].filter(Boolean).join(" · "),
      title: [bankName, account.label, via].filter(Boolean).join(" · "),
    });
  }
  return lines;
}

// A CSV bank is stale when its latest line is more than this many days before
// the end of the bank range — enough slack for a weekend with no postings.
const STALE_AFTER_DAYS = 3;

// Group the clinics' active bank accounts into feed cards. Accounts on a bank
// with no feed here (TD, BNS, CIBC, …) are left out. Connected feeds also show
// how many deposit lines came in and the latest date among them (`dataUpTo`).
function buildBankFeeds(accounts, bankLines, loadedFeeds, bankRangeTo) {
  return FEED_TYPES.map((ft) => {
    // Bank accounts only — not the clinic's RBC Visa credit card.
    const onBank = accounts.filter((a) => a.bank_type === ft.type && a.account_type === "bank");
    if (onBank.length === 0) return null;
    const lines = bankLines.filter((l) => l.account.bank_type === ft.type);
    const loaded = loadedFeeds.has(ft.feed);
    const dataUpTo = lines.reduce((max, l) => (l.date > max ? l.date : max), "") || null;
    return {
      ...ft,
      ...FEED_STATUS[ft.feed],
      accounts: onBank.length,
      practices: new Set(onBank.map((a) => a.clinic_code).filter(Boolean)).size,
      dataUpTo,
      // NetSuite refreshes itself; a CSV bank needs a new file once it falls
      // behind. Unknown while its lines are still loading.
      needsCsv:
        ft.feed !== "netsuite" &&
        (ft.feed === "none" ||
          (loaded && (!dataUpTo || dataUpTo < addDays(bankRangeTo, -STALE_AFTER_DAYS)))),
      ...(loaded && {
        matched: 0,
        total: lines.length,
        amount: lines.reduce((sum, l) => sum + l.amount, 0),
      }),
    };
  }).filter(Boolean);
}

// POST body for a match: lines as the backend stores them.
function matchPayload({ practiceId, deposits, banks }) {
  const line = (l, description) => ({
    key: l.id,
    date: l.date,
    amount: l.amount.toFixed(2),
    description,
  });
  return {
    clinic: practiceId,
    deposits: deposits.map((d) => line(d, d.method)),
    bank: banks.map((b) => line(b, b.description)),
  };
}

// Collection Breakdown fields that become deposit lines, with their Deposit
// Sheet labels (shared with Deposit Sheet's Bank columns).
const DEPOSIT_FIELDS = DEPOSIT_SHEET_BANK_FIELDS;

// One group per selected clinic across the chosen dates: its Collection
// Breakdown lines (one per non-zero payment type per day) matched against its
// bank deposit lines. Clinics with nothing on either side are dropped.
function buildPracticeGroups(
  clinics,
  accounts,
  breakdownByClinic,
  bankLines,
  loadedFeeds,
  loadingFeeds,
  savedMatches,
  insurancePayments,
) {
  return clinics
    .map((code) => {
      const clinicAccounts = accounts.filter((a) => a.clinic_code === code);
      const feeds = [
        ...new Map(
          clinicAccounts
            .map((a) => FEED_BY_TYPE.get(a.bank_type))
            .filter(Boolean)
            .map((ft) => [ft.type, ft]),
        ).values(),
      ];
      const bankNames = feeds.map((ft) => ft.name).join(", ");
      const hasLoadedFeed = feeds.some((ft) => loadedFeeds.has(ft.feed));
      const bankLoading = feeds.some((ft) => loadingFeeds.has(ft.feed));

      // Insurance EFTs / cheques read from the day's uploaded files (the same
      // rows the Deposit Sheet modal lists), grouped by "<date>:<field>". They
      // replace that day's Collection Breakdown total, one line per payment,
      // only when they add up to it; otherwise the entered total wins.
      const payments = insurancePayments?.clinic === code ? insurancePayments.data : null;
      const paymentsByDay = new Map();
      for (const p of payments?.results ?? []) {
        const k = `${p.date}:${p.field}`;
        if (!paymentsByDay.has(k)) paymentsByDay.set(k, []);
        paymentsByDay.get(k).push(p);
      }

      const deposits = (breakdownByClinic[code] || []).flatMap((record) =>
        DEPOSIT_FIELDS.map((f) => {
          // A refund paid back through this method comes off its deposit
          // (same rule as the backend's deposit_match _resolve_deposit).
          const refund = roundTo2(refundForField(record, f.key));
          return { f, refund, amount: roundTo2((Number(record[f.key]) || 0) - refund) };
        })
          .filter(({ amount }) => amount !== 0)
          .flatMap(({ f, refund, amount }) => {
            // "<date>:<field>" — the key the backend stores on a manual match.
            const id = `${record.date}:${f.key}`;
            const read = paymentsByDay.get(id) ?? [];
            const readTotal = roundTo2(read.reduce((s, p) => s + (Number(p.amount) || 0), 0));
            // With a refund the per-payment lines no longer add up to what was
            // deposited, so the net entered total is shown instead.
            if (read.length && !refund && readTotal === amount) {
              return read.map((p) => {
                const label = p.kind === "cheque" ? "Insurance cheque" : "Insurance EFT";
                return {
                  // "<date>:<field>:<scan>:<n>" — re-checked against the file on save.
                  id: p.key,
                  date: p.date,
                  method: p.payer || label,
                  ref: [p.reference, p.needs_review && "check file"].filter(Boolean).join(" · "),
                  title: `${label} read from the day's uploaded files`,
                  amount: roundTo2(Number(p.amount) || 0),
                };
              });
            }
            return [
              {
                id,
                date: record.date,
                method: f.label,
                ref:
                  [
                    refund && `less refund ${refund.toFixed(2)}`,
                    read.length && `files read ${readTotal.toFixed(2)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined,
                title: refund
                  ? "Entered total less the day's refund"
                  : read.length
                    ? "Entered total differs from the uploaded files — using the entered total"
                    : undefined,
                amount,
              },
            ];
          }),
      );
      const bank = bankLines
        .filter((l) => l.account.clinic_code === code)
        .map((l) => ({
          id: l.id,
          date: l.date,
          description: l.description,
          amount: l.amount,
          title: l.title,
        }));

      const saved = savedMatches.filter((m) => m.clinic_code === code);
      const rows = matchDeposits(deposits, bank, saved);

      return {
        id: code,
        name: clinicAccounts[0]?.clinic_name || code,
        bank: bankNames || "No bank account",
        manager: code,
        rows,
        stats: practiceStats(rows),
        // A clinic whose bank isn't connected can't be over/short — say why.
        bankNote:
          feeds.length === 0
            ? "No RBC, BMO, Desjardins or Meridian account"
            : bankLoading
              ? `Loading ${bankNames} bank lines…`
              : hasLoadedFeed
                ? null
                : `${bankNames} isn't connected yet — bank lines coming later`,
      };
    })
    .filter((g) => g.rows.length > 0);
}

/**
 * DailyDeposits — finance sub-tab for a date window across the selected clinics,
 * day by day: each clinic's Collection Breakdown (left) beside the deposits
 * that reached its bank accounts (right).
 *
 * Nothing is selected → nothing renders, matching the other finance tabs.
 */
export default function DailyDeposits({ range, clinics = [] }) {
  const { user } = useAuth();
  // `range` is the filter bar's from / to dates, { from, to }.
  const today = torontoToday();

  // Deposits cover the window; the bank side runs MATCH_WINDOW_DAYS past it
  // (capped at today) so deposits at the end of the window can still find their
  // bank line.
  const depositRange = useMemo(() => range ?? { from: "", to: "" }, [range]);
  const bankRange = useMemo(() => {
    if (!range) return { from: "", to: "" };
    const end = addDays(range.to, MATCH_WINDOW_DAYS);
    // Starts MATCH_BACK_DAYS early: a bank line can carry a date before its
    // collection (see depositAutoMatch.js).
    return { from: addDays(range.from, -MATCH_BACK_DAYS), to: end > today ? today : end };
  }, [range, today]);
  const hasRanges = !!(depositRange.from && depositRange.to && bankRange.from && bankRange.to);
  const clinicCodes = useMemo(
    () => [...new Set(clinics.filter(Boolean).map((c) => String(c).toUpperCase()))].sort(),
    [clinics],
  );
  const hasClinics = clinicCodes.length > 0;

  const accountsQuery = useBankAccounts(
    { status: "active", has_clinic: "true", clinic: clinicCodes.join(","), limit: 500 },
    { enabled: hasClinics },
  );
  const breakdownQuery = useMultiClinicCollectionBreakdowns(
    clinicCodes,
    { date_from: depositRange.from, date_to: depositRange.to },
    { enabled: hasClinics && hasRanges },
  );
  // One clinic at a time (the filter bar enforces it): one request per window.
  const insurancePaymentsQuery = useInsurancePayments(
    { clinic: clinicCodes[0], date_from: depositRange.from, date_to: depositRange.to },
    { enabled: hasClinics && hasRanges },
  );

  // Saved manual matches touching either side's dates. One clinic at a time
  // (the filter bar enforces it), so this is a single small DB query.
  // Every date a deposit or bank line can carry.
  const matchRange = {
    date_from: depositRange.from < bankRange.from ? depositRange.from : bankRange.from,
    date_to: depositRange.to > bankRange.to ? depositRange.to : bankRange.to,
  };
  const matchesQuery = useDepositMatches(
    { clinic: clinicCodes[0], ...matchRange },
    { enabled: hasClinics && hasRanges },
  );

  const accounts = useMemo(
    () => accountsQuery.data?.results ?? accountsQuery.data ?? [],
    [accountsQuery.data],
  );
  // Every active clinic account, not just the selected clinics'. Same filters
  // as Daily Bank Balance, so the list is usually already cached.
  // The Bank feeds modal covers every clinic, so opening it loads bank data
  // even with nothing selected.
  const [feedsOpen, setFeedsOpen] = useState(false);
  const allAccountsQuery = useBankAccounts(
    { status: "active", has_clinic: "true", limit: 500 },
    { enabled: hasClinics || feedsOpen },
  );
  const allAccounts = useMemo(
    () => allAccountsQuery.data?.results ?? allAccountsQuery.data ?? [],
    [allAccountsQuery.data],
  );
  // RBC accounts for EVERY clinic, not the selection: the request is then the
  // same whatever is selected and stays in react-query's cache, and the Bank
  // feeds modal can count every clinic's lines. Lines are narrowed to the
  // selected clinics by `normalizeBankLines`, which only keeps accounts in
  // `accounts`.
  const rbcBankIds = useMemo(
    () =>
      allAccounts
        // Bank accounts only: each clinic also has an RBC Visa credit card
        // tagged bank_type RBC, whose payment credits are not deposits.
        .filter(
          (a) =>
            a.bank_type === "RBC" && a.account_type === "bank" && a.netsuite_account_id != null,
        )
        .map((a) => a.id),
    [allAccounts],
  );
  // Every clinic's Meridian, BMO and Desjardins accounts too (a handful), for
  // the same reasons. Deposits only, where RBC also needs its negative card
  // batches — hence a query of their own.
  const dbBankIds = useMemo(
    () =>
      allAccounts
        .filter(
          (a) =>
            a.account_type === "bank" &&
            ["sync", "csv"].includes(FEED_BY_TYPE.get(a.bank_type)?.feed),
        )
        .map((a) => a.id),
    [allAccounts],
  );

  // Keyed on the bank range only (default: derived from the window), never on
  // the clinic selection, so it stays cached across clinic changes.
  // No limit: every line in the range, as the NetSuite feed used to return.
  const rbcQuery = useBankDailyTransactions(
    { bank_id: rbcBankIds.join(","), date_from: bankRange.from, date_to: bankRange.to },
    { enabled: hasRanges && rbcBankIds.length > 0 },
  );
  const dbQuery = useBankDailyTransactions(
    {
      bank_id: dbBankIds.join(","),
      date_from: bankRange.from,
      date_to: bankRange.to,
      direction: "deposit",
      limit: 1000,
    },
    { enabled: hasRanges && dbBankIds.length > 0 },
  );

  // A feed counts as loaded once its query has answered (an account set with
  // no ids never queries, and simply has no lines).
  const loadedFeeds = useMemo(() => {
    const set = new Set();
    if (rbcQuery.isSuccess) set.add("netsuite");
    if (dbQuery.isSuccess) {
      set.add("sync");
      set.add("csv");
    }
    return set;
  }, [rbcQuery.isSuccess, dbQuery.isSuccess]);

  // Feeds still fetching. The Collection Breakdown renders without waiting for
  // them (the NetSuite call is the slow one); their rows say "Loading bank
  // lines…" until the bank side arrives.
  const rbcLoading =
    allAccountsQuery.isLoading || (rbcQuery.isLoading && rbcQuery.fetchStatus !== "idle");
  const dbLoading = dbQuery.isLoading && dbQuery.fetchStatus !== "idle";
  const loadingFeeds = useMemo(() => {
    const set = new Set();
    if (rbcLoading) set.add("netsuite");
    if (dbLoading) {
      set.add("sync");
      set.add("csv");
    }
    return set;
  }, [rbcLoading, dbLoading]);

  const bankLines = useMemo(
    () => normalizeBankLines(accounts, rbcQuery.data?.results ?? [], dbQuery.data?.results ?? []),
    [accounts, rbcQuery.data, dbQuery.data],
  );
  const bankFeeds = useMemo(
    () => buildBankFeeds(accounts, bankLines, loadedFeeds, bankRange.to),
    [accounts, bankLines, loadedFeeds, bankRange.to],
  );
  // Same, across every clinic — what the Bank feeds modal lists.
  const allBankFeeds = useMemo(
    () =>
      buildBankFeeds(
        allAccounts,
        normalizeBankLines(allAccounts, rbcQuery.data?.results ?? [], dbQuery.data?.results ?? []),
        loadedFeeds,
        bankRange.to,
      ),
    [allAccounts, rbcQuery.data, dbQuery.data, loadedFeeds, bankRange.to],
  );
  // Uploading a bank CSV writes bank lines: same audience as manual matching.
  const canUpload = canManageDailyBankBalance(user);
  const uploadCsv = useUploadBankCsv();
  const [uploadNote, setUploadNote] = useState(null);
  // A chosen CSV is only read first (preview: nothing written); its lines are
  // shown, and only "Post" sends the same file again to write them.
  const [csvPreview, setCsvPreview] = useState(null);
  const handleUpload = async (feed, file) => {
    setUploadNote(null);
    try {
      const data = await uploadCsv.mutateAsync({ bankType: feed.type, file, preview: true });
      setCsvPreview({ feed, file, data });
    } catch (e) {
      setUploadNote({ tone: "error", text: `${file.name}: ${getApiErrorMessage(e)}` });
    }
  };
  const handlePost = async () => {
    const { feed, file } = csvPreview;
    try {
      const r = await uploadCsv.mutateAsync({ bankType: feed.type, file });
      setUploadNote({
        tone: "ok",
        text:
          `Posted ${r.inserted} ${r.inserted === 1 ? "line" : "lines"} from ${file.name}` +
          ` · ${r.date_from} – ${r.date_to} · ` +
          (r.accounts?.length > 1 ? `${r.accounts.length} accounts` : r.bank_account.label),
      });
    } catch (e) {
      setUploadNote({ tone: "error", text: `${file.name}: ${getApiErrorMessage(e)}` });
    }
    setCsvPreview(null);
  };
  const bankFeedsModal = (
    <BankFeedsModal
      isOpen={feedsOpen}
      onClose={() => {
        setFeedsOpen(false);
        setUploadNote(null);
        setCsvPreview(null);
      }}
      feeds={allBankFeeds}
      loading={allAccountsQuery.isLoading}
      onUpload={canUpload ? handleUpload : undefined}
      uploadingType={
        uploadCsv.isPending && uploadCsv.variables?.preview ? uploadCsv.variables.bankType : null
      }
      uploadNote={uploadNote}
      preview={csvPreview}
      onPost={handlePost}
      onCancelPreview={() => setCsvPreview(null)}
      posting={uploadCsv.isPending && !uploadCsv.variables?.preview}
    />
  );
  const practices = useMemo(
    () =>
      buildPracticeGroups(
        clinicCodes,
        accounts,
        breakdownQuery.data?.results ?? {},
        bankLines,
        loadedFeeds,
        loadingFeeds,
        matchesQuery.data ?? [],
        { clinic: clinicCodes[0], data: insurancePaymentsQuery.data },
      ),
    [
      clinicCodes,
      accounts,
      breakdownQuery.data,
      bankLines,
      loadedFeeds,
      loadingFeeds,
      matchesQuery.data,
      insurancePaymentsQuery.data,
    ],
  );

  // The selected clinic's Collection Breakdown, for the card beside the bank feeds.
  const breakdownRecords = useMemo(
    () => clinicCodes.flatMap((code) => breakdownQuery.data?.results?.[code] ?? []),
    [clinicCodes, breakdownQuery.data],
  );

  // Manual matching — same write audience as the bank data it is made from.
  const canMatch = canManageDailyBankBalance(user);
  const createMatch = useCreateDepositMatch();
  const deleteMatch = useDeleteDepositMatch();
  const bulkMatch = useBulkCreateDepositMatches();
  // Many suggestions → one bulk request per clinic. Resolves to how many were
  // saved; lines taken meanwhile are skipped by the backend.
  const saveSuggestions = async (items, { auto = false } = {}) => {
    const byClinic = new Map();
    for (const { practiceId, row } of items) {
      const payload = matchPayload({ practiceId, deposits: row.deposits, banks: row.banks });
      if (!byClinic.has(practiceId)) byClinic.set(practiceId, []);
      byClinic.get(practiceId).push({ deposits: payload.deposits, bank: payload.bank });
    }
    let saved = 0;
    for (const [clinic, matches] of byClinic) {
      for (let i = 0; i < matches.length; i += 500) {
        const r = await bulkMatch.mutateAsync({ clinic, auto, matches: matches.slice(i, i + 500) });
        saved += r.created.length;
      }
    }
    return saved;
  };
  const [matchError, setMatchError] = useState(null);
  const handleCreateMatch = async ({ practiceId, deposits, banks }) => {
    setMatchError(null);
    try {
      await createMatch.mutateAsync(matchPayload({ practiceId, deposits, banks }));
    } catch (e) {
      setMatchError(getApiErrorMessage(e));
      throw e;
    }
  };
  // Accepting a suggestion saves it as a normal match; a `near` one keeps
  // its difference as the match's variance.
  const [acceptingId, setAcceptingId] = useState(null);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const handleAcceptSuggestion = async ({ practiceId, row }) => {
    setAcceptingId(row.id);
    try {
      await handleCreateMatch({ practiceId, deposits: row.deposits, banks: row.banks });
    } catch {
      // handleCreateMatch already set matchError.
    } finally {
      setAcceptingId(null);
    }
  };
  const handleAcceptAll = async (items) => {
    setAcceptingAll(true);
    setMatchError(null);
    try {
      const saved = await saveSuggestions(items);
      if (saved < items.length) {
        setMatchError(`${items.length - saved} could not be saved — reload to see why.`);
      }
    } catch (e) {
      setMatchError(getApiErrorMessage(e));
    } finally {
      setAcceptingAll(false);
    }
  };

  // Only the deposit side gates the panel; bank feeds fill in afterwards.
  const isLoadingPanel =
    accountsQuery.isLoading ||
    breakdownQuery.isLoading ||
    matchesQuery.isLoading ||
    // Wait for the read files too, or insurance lines would swap under the user.
    insurancePaymentsQuery.isLoading;

  // Auto-match: every suggestion whose totals agree to the cent is saved on
  // its own (`auto`), once all bank lines and saved matches are in. Lines
  // someone unmatched are skipped, so unmatching a wrong one sticks; they stay
  // suggested for a manual Accept. Write audience only.
  const dismissedQuery = useDepositMatchDismissals(
    { clinic: clinicCodes[0], date_from: matchRange.date_from, date_to: matchRange.date_to },
    { enabled: canMatch && hasClinics && hasRanges },
  );
  const autoTried = useRef(new Set());
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoMatched, setAutoMatched] = useState(0);
  const autoReady =
    canMatch &&
    !autoRunning &&
    !isLoadingPanel &&
    loadingFeeds.size === 0 &&
    matchesQuery.isSuccess &&
    !matchesQuery.isFetching &&
    dismissedQuery.isSuccess &&
    !dismissedQuery.isFetching &&
    !createMatch.isPending &&
    !bulkMatch.isPending &&
    !deleteMatch.isPending;
  useEffect(() => {
    if (!autoReady) return;
    const dismissed = new Set(dismissedQuery.data ?? []);
    const signature = (row) => [...row.deposits, ...row.banks].map((l) => l.id).join("|");
    const todo = practices
      .flatMap((p) => p.rows.map((row) => ({ practiceId: p.id, row })))
      .filter(
        ({ row }) =>
          row.status === "suggested" &&
          row.variance === 0 &&
          ![...row.deposits, ...row.banks].some((l) => dismissed.has(l.id)) &&
          !autoTried.current.has(signature(row)),
      );
    if (todo.length === 0) return;
    todo.forEach(({ row }) => autoTried.current.add(signature(row)));
    setAutoRunning(true);
    (async () => {
      try {
        const saved = await saveSuggestions(todo, { auto: true });
        setAutoMatched((n) => n + saved);
      } catch {
        // Left as suggestions; not retried (autoTried).
      } finally {
        setAutoRunning(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoReady, practices, dismissedQuery.data]);

  const handleUnmatch = async (matchId) => {
    setMatchError(null);
    try {
      await deleteMatch.mutateAsync(matchId);
    } catch (e) {
      setMatchError(getApiErrorMessage(e));
    }
  };
  const bankErrors = [
    rbcQuery.isError && `RBC: ${getApiErrorMessage(rbcQuery.error)}`,
    dbQuery.isError && `Meridian / BMO: ${getApiErrorMessage(dbQuery.error)}`,
    matchesQuery.isError && `Saved matches: ${getApiErrorMessage(matchesQuery.error)}`,
    insurancePaymentsQuery.isError &&
      `Insurance files: ${getApiErrorMessage(insurancePaymentsQuery.error)}`,
  ].filter(Boolean);

  if (!hasClinics) {
    // Uploading a bank CSV doesn't depend on the clinic selection.
    return (
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <p className="flex-1 text-sm text-palette-slate-secondary">
            Select a clinic to view daily deposits.
          </p>
          <BankFeedsButton onClick={() => setFeedsOpen(true)} />
        </div>
        {bankFeedsModal}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Bank feeds, and under them the clinic's Collection Breakdown
          coloured by match status. */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2.5 px-0.5">
            <span className="text-sm font-semibold text-palette-slate-primary">Bank feeds</span>
            <span className="text-[12.5px] text-palette-slate-placeholder">
              RBC from NetSuite · Meridian from the daily sync · BMO from uploaded CSVs
            </span>
          </div>
          {accountsQuery.isLoading ? (
            <Loading />
          ) : accountsQuery.isError ? (
            <p className="text-sm text-palette-rose-heading">
              {getApiErrorMessage(accountsQuery.error)}
            </p>
          ) : bankFeeds.length === 0 ? (
            <p className="text-sm text-palette-slate-secondary">
              No RBC, BMO, Desjardins or Meridian accounts for the selected clinics.
            </p>
          ) : (
            // Fixed-width cards that wrap, so one or two cards don't stretch across
            // the page. Full width only on phones, where 320px is the whole screen.
            <div className="flex flex-wrap gap-3">
              {bankFeeds.map((feed) => (
                <div key={feed.type} className="w-full sm:w-80">
                  <BankFeedCard {...feed} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <CollectionBreakdownCard
            records={breakdownRecords}
            fields={DEPOSIT_FIELDS}
            practices={practices}
            loading={breakdownQuery.isLoading}
          />
        </div>
      </div>
      {autoMatched > 0 && (
        <p className="m-0 text-[13px] text-palette-emerald-hover">
          Auto-matched {autoMatched} exact-amount {autoMatched === 1 ? "suggestion" : "suggestions"}{" "}
          — Unmatch any that are wrong; they won’t be auto-matched again.
        </p>
      )}
      {bankErrors.map((msg) => (
        <p key={msg} className="m-0 text-sm text-palette-rose-heading">
          {msg}
        </p>
      ))}
      {isLoadingPanel ? (
        <Loading />
      ) : breakdownQuery.isError ? (
        <p className="text-sm text-palette-rose-heading">
          {getApiErrorMessage(breakdownQuery.error)}
        </p>
      ) : (
        <DepositMatchPanel
          practices={practices}
          bankRange={bankRange}
          onOpenBankFeeds={() => setFeedsOpen(true)}
          canMatch={canMatch}
          onCreateMatch={handleCreateMatch}
          onUnmatch={handleUnmatch}
          matching={createMatch.isPending}
          unmatchingId={deleteMatch.isPending ? deleteMatch.variables : null}
          matchError={matchError}
          onAcceptSuggestion={handleAcceptSuggestion}
          onAcceptAll={handleAcceptAll}
          acceptingId={acceptingId}
          acceptingAll={acceptingAll}
        />
      )}
      {bankFeedsModal}
    </div>
  );
}
